const META_PIXEL_SCRIPT_ID = "zoco-meta-pixel-script";
const META_PIXEL_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const META_PIXEL_ID_RE = /^\d{10,20}$/;
const META_PIXEL_ACTIVE_ID_KEY = "__zocoActiveMetaPixelId";
export const DEFAULT_META_PIXEL_ID = "1563949418768121";

const getRuntime = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return null;
  return { window, document };
};

export const normalizeMetaPixelId = (value) => String(value || "").trim();

export const isValidMetaPixelId = (value) => META_PIXEL_ID_RE.test(normalizeMetaPixelId(value));

export const isMetaPixelScriptLike = (value) =>
  /<|>|script|fbq|javascript|connect\.facebook\.net/i.test(String(value || ""));

export const getMetaPixelConfig = (eventLike = {}) => {
  const config =
    eventLike?.marketing?.metaPixel ||
    eventLike?.marketing?.integrations?.metaPixel ||
    eventLike?.metaPixel ||
    null;
  const pixelId = normalizeMetaPixelId(config?.pixelId);
  return {
    enabled: Boolean(config?.enabled),
    pixelId,
    isConfigured: Boolean(config?.enabled && isValidMetaPixelId(pixelId)),
  };
};

export const getResolvedMetaPixelId = (eventLike = {}) => {
  const config = getMetaPixelConfig(eventLike);
  if (config.isConfigured) return config.pixelId;
  return DEFAULT_META_PIXEL_ID;
};

export const loadMetaPixel = (pixelId = DEFAULT_META_PIXEL_ID) => {
  const runtime = getRuntime();
  const normalizedPixelId = normalizeMetaPixelId(pixelId);
  if (!runtime || !isValidMetaPixelId(normalizedPixelId)) return false;

  const { window: win, document: doc } = runtime;
  win.__zocoMetaPixelIds = win.__zocoMetaPixelIds || {};

  if (!win.fbq) {
    const fbq = function fbq() {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, arguments);
      } else {
        fbq.queue.push(arguments);
      }
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    win.fbq = fbq;
    win._fbq = fbq;
  }

  const hasExistingScript =
    doc.getElementById(META_PIXEL_SCRIPT_ID) ||
    doc.querySelector(`script[src="${META_PIXEL_SRC}"]`);

  if (!hasExistingScript) {
    const script = doc.createElement("script");
    script.id = META_PIXEL_SCRIPT_ID;
    script.async = true;
    script.src = META_PIXEL_SRC;
    const firstScript = doc.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      doc.head.appendChild(script);
    }
  }

  if (!win.__zocoMetaPixelIds[normalizedPixelId]) {
    const shouldAssumeDefaultAlreadyInitialized =
      normalizedPixelId === DEFAULT_META_PIXEL_ID &&
      typeof win.fbq === "function" &&
      hasExistingScript;

    if (!shouldAssumeDefaultAlreadyInitialized) {
      win.fbq("init", normalizedPixelId);
    }
    // Important in SPA: avoid automatic history PageView tracking on unrelated routes.
    try {
      win.fbq("set", "autoConfig", false, normalizedPixelId);
      win.fbq("set", "disablePushState", true, normalizedPixelId);
    } catch (_) {}
    win.__zocoMetaPixelIds[normalizedPixelId] = true;
  }
  win[META_PIXEL_ACTIVE_ID_KEY] = normalizedPixelId;

  return true;
};

export const clearMetaPixelActive = () => {
  const runtime = getRuntime();
  if (!runtime) return;
  delete runtime.window[META_PIXEL_ACTIVE_ID_KEY];
};

export const unloadMetaPixel = () => {
  const runtime = getRuntime();
  if (!runtime) return;
  const { window: win, document: doc } = runtime;
  const script = doc.getElementById(META_PIXEL_SCRIPT_ID);
  if (script?.parentNode) {
    script.parentNode.removeChild(script);
  }
  delete win[META_PIXEL_ACTIVE_ID_KEY];
  delete win.__zocoMetaPixelIds;
  delete win.fbq;
  delete win._fbq;
};

export const trackMetaPixel = (eventName, params, options) => {
  const runtime = getRuntime();
  if (!runtime || !runtime.window.fbq || !eventName) return false;
  const activePixelId = normalizeMetaPixelId(runtime.window[META_PIXEL_ACTIVE_ID_KEY]);

  if (isValidMetaPixelId(activePixelId)) {
    if (options !== undefined) {
      runtime.window.fbq("trackSingle", activePixelId, eventName, params || {}, options);
    } else if (params !== undefined) {
      runtime.window.fbq("trackSingle", activePixelId, eventName, params);
    } else {
      runtime.window.fbq("trackSingle", activePixelId, eventName);
    }
    return true;
  }

  if (options !== undefined) {
    runtime.window.fbq("track", eventName, params || {}, options);
  } else if (params !== undefined) {
    runtime.window.fbq("track", eventName, params);
  } else {
    runtime.window.fbq("track", eventName);
  }
  return true;
};

const readCookie = (name) => {
  const runtime = getRuntime();
  if (!runtime) return "";
  const match = String(runtime.document.cookie || "").match(
    new RegExp(`(?:^|;\\s*)${name}=([^;]+)`)
  );
  return match ? decodeURIComponent(match[1]) : "";
};

/**
 * Browser identifiers Meta uses to match a server-side conversion back to the
 * user. Sent with the checkout request so the backend can attach them to the
 * Purchase event it reports through the Conversions API.
 */
export const getMetaBrowserSignals = () => {
  const runtime = getRuntime();
  const signals = {
    fbp: readCookie("_fbp"),
    fbc: readCookie("_fbc"),
    sourceUrl: runtime ? String(runtime.window.location?.href || "") : "",
  };
  return Object.fromEntries(Object.entries(signals).filter(([, value]) => value));
};

/**
 * Purchase parameters for the browser pixel, built from the backend order
 * status payload. `eventID` MUST match the server-side `event_id` or Meta
 * counts the conversion twice.
 */
export const buildMetaPurchasePayload = (orderInfo = {}) => {
  const params = {
    value: Number(
      orderInfo.value ||
        orderInfo.chargeTotal ||
        orderInfo.paidamount ||
        orderInfo.totalamount ||
        0
    ),
    currency: normalizeMetaCurrency(orderInfo.currency || "ARS"),
  };

  if (Array.isArray(orderInfo.metaContentIds) && orderInfo.metaContentIds.length > 0) {
    params.content_ids = orderInfo.metaContentIds;
    params.content_type = orderInfo.metaContentType || "product";
  }
  if (Number(orderInfo.metaNumItems) > 0) params.num_items = Number(orderInfo.metaNumItems);
  if (orderInfo.orderNumber) params.order_id = String(orderInfo.orderNumber);

  const options = orderInfo.metaEventId ? { eventID: String(orderInfo.metaEventId) } : undefined;

  return { params, options };
};

export const normalizeMetaCurrency = (value) => {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw || raw === "$" || raw === "AR$") return "ARS";
  if (/^[A-Z]{3}$/.test(raw)) return raw;
  return "ARS";
};
